import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Palette as PaletteIcon, Lock as LockIcon, ShoppingCart as CartIcon, Close as CloseIcon, Visibility as PreviewIcon } from '@mui/icons-material';
import type { ThemeWithOwnership, ThemeCategory } from '../../types';
import { THEME_CATEGORIES } from '../../types';
import { ThemePreview } from './ThemePreview';
import { ThemeFullPreview } from './ThemeFullPreview';
import { CreditsPurchaseModal } from '../checkout';
import { useThemes, useOwnedThemes, useCredits } from '../../hooks';
import { themesApi } from '../../api/themes';

interface ThemeSelectorProps {
  selectedThemeId?: string | null;
  onSelect: (themeId: string | null) => void;
  onPurchaseClick?: (theme: ThemeWithOwnership) => void;
  showPurchaseButton?: boolean;
  disabled?: boolean;
  enableInlinePurchase?: boolean; // When true, allows purchasing directly in the selector
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  selectedThemeId,
  onSelect,
  onPurchaseClick,
  showPurchaseButton = true,
  disabled = false,
  enableInlinePurchase = true,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ThemeCategory | 'owned'>('classic');
  const [purchaseTheme, setPurchaseTheme] = useState<ThemeWithOwnership | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);
  const [previewTheme, setPreviewTheme] = useState<ThemeWithOwnership | null>(null);
  const [creditsPurchaseOpen, setCreditsPurchaseOpen] = useState(false);
  const { themes, isLoading, error, refresh: refreshThemes } = useThemes();
  const { themes: ownedThemes, refresh: refreshOwnedThemes } = useOwnedThemes();
  const { credits, refreshCredits } = useCredits();

  const selectedTheme = themes.find(t => t.id === selectedThemeId);

  // Filter themes by category
  const getThemesByCategory = (category: ThemeCategory | 'owned') => {
    if (category === 'owned') {
      return ownedThemes;
    }
    return themes.filter(t => t.category === category && t.active);
  };

  // Get available (owned or free) themes for selection
  const availableThemes = themes.filter(t => t.owned || t.isFree);

  const handleThemeSelect = (theme: ThemeWithOwnership) => {
    if (theme.owned || theme.isFree) {
      onSelect(theme.id);
      setDialogOpen(false);
    } else if (enableInlinePurchase) {
      // Show inline purchase dialog
      setPurchaseTheme(theme);
      setPurchaseError(null);
      setPurchaseSuccess(null);
    } else if (showPurchaseButton && onPurchaseClick) {
      onPurchaseClick(theme);
    }
  };

  const handlePurchaseConfirm = async () => {
    if (!purchaseTheme) return;

    setIsPurchasing(true);
    setPurchaseError(null);

    try {
      await themesApi.purchaseTheme(purchaseTheme.id);
      setPurchaseSuccess(`Successfully purchased "${purchaseTheme.name}"!`);

      // Refresh data
      await Promise.all([
        refreshThemes(),
        refreshOwnedThemes(),
        refreshCredits(),
      ]);

      // Auto-apply the theme after a brief delay
      setTimeout(() => {
        onSelect(purchaseTheme.id);
        setPurchaseTheme(null);
        setPurchaseSuccess(null);
        setDialogOpen(false);
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to purchase theme';
      setPurchaseError(message);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handlePurchaseCancel = () => {
    setPurchaseTheme(null);
    setPurchaseError(null);
    setPurchaseSuccess(null);
  };

  const handleClearTheme = () => {
    onSelect(null);
    setDialogOpen(false);
  };

  const displayThemes = getThemesByCategory(activeTab);

  return (
    <Box>
      {/* Theme Selector Button */}
      <Box
        onClick={() => !disabled && setDialogOpen(true)}
        sx={{
          p: 2,
          border: '2px dashed',
          borderColor: disabled ? 'action.disabled' : 'primary.main',
          borderRadius: 2,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease',
          '&:hover': disabled ? {} : {
            borderColor: 'primary.dark',
            bgcolor: 'action.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {selectedTheme ? (
            <>
              <ThemePreview
                theme={selectedTheme}
                size="small"
                showOwnershipBadge={false}
                hideInfo
                sx={{ border: 0, p: 0, bgcolor: 'transparent' }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {selectedTheme.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedTheme.category} theme
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearTheme();
                }}
                disabled={disabled}
              >
                Remove
              </Button>
            </>
          ) : (
            <>
              <PaletteIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Select a Theme
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {availableThemes.length} themes available
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Theme Selection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            Select Board Theme
            {selectedTheme && (
              <>
                <Chip
                  label={`Current: ${selectedTheme.name}`}
                  size="small"
                />
                <Button
                  size="small"
                  color="error"
                  onClick={handleClearTheme}
                  sx={{ ml: 1 }}
                >
                  Remove
                </Button>
              </>
            )}
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Category Tabs - only show categories with themes */}
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab value="owned" label={`My Themes (${ownedThemes.length})`} />
                {THEME_CATEGORIES.map(cat => {
                  const count = themes.filter(t => t.category === cat.value && t.active).length;
                  // Only show categories that have active themes
                  if (count === 0) return null;
                  return (
                    <Tab
                      key={cat.value}
                      value={cat.value}
                      label={`${cat.label} (${count})`}
                    />
                  );
                })}
              </Tabs>

              {/* Credits display */}
              {credits && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CartIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2">
                    <strong>{credits.availableCredits}</strong> credits available
                  </Typography>
                </Box>
              )}

              {/* Theme Grid */}
              {displayThemes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    {activeTab === 'owned'
                      ? 'You haven\'t purchased any themes yet. Browse the categories to find themes you like!'
                      : 'No themes available in this category.'}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {displayThemes.map(theme => {
                    const isLocked = !theme.isFree && !theme.owned;
                    const canAfford = credits ? credits.availableCredits >= theme.creditPrice : false;
                    return (
                      <Grid key={theme.id} size={{ xs: 6, sm: 4, md: 3 }}>
                        <Box sx={{ position: 'relative' }}>
                          <ThemePreview
                            theme={theme}
                            selected={theme.id === selectedThemeId}
                            onClick={() => handleThemeSelect(theme)}
                            disabled={isLocked && !enableInlinePurchase && !showPurchaseButton}
                            isMyThemesView={activeTab === 'owned'}
                          />
                          {/* Preview button */}
                          <Tooltip title="Preview theme">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewTheme(theme);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                p: 0.5,
                                '&:hover': { bgcolor: 'grey.100' },
                              }}
                            >
                              <PreviewIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                          {isLocked && (enableInlinePurchase || showPurchaseButton) && (
                            <Button
                              size="small"
                              variant="contained"
                              color={canAfford ? 'primary' : 'inherit'}
                              startIcon={canAfford ? <CartIcon /> : <LockIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleThemeSelect(theme);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                fontSize: '0.6rem',
                                py: 0.25,
                                px: 0.75,
                                minWidth: 'auto',
                                '& .MuiButton-startIcon': {
                                  mr: 0.5,
                                  '& > svg': { fontSize: '0.85rem' },
                                },
                                ...(canAfford ? {} : {
                                  bgcolor: 'grey.400',
                                  '&:hover': { bgcolor: 'grey.500' },
                                }),
                              }}
                            >
                              {canAfford ? `${theme.creditPrice}` : 'Locked'}
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Purchase Confirmation Dialog */}
      <Dialog
        open={!!purchaseTheme}
        onClose={handlePurchaseCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Unlock Theme
        </DialogTitle>
        <DialogContent>
          {purchaseSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {purchaseSuccess}
            </Alert>
          ) : (
            <>
              {purchaseError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {purchaseError}
                </Alert>
              )}
              {purchaseTheme && (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <ThemePreview
                      theme={purchaseTheme}
                      size="medium"
                      showOwnershipBadge={false}
                      hideInfo
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {purchaseTheme.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {purchaseTheme.description}
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Cost:</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {purchaseTheme.creditPrice} credit{purchaseTheme.creditPrice !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Your balance:</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={credits && credits.availableCredits >= purchaseTheme.creditPrice ? 'success.main' : 'error.main'}
                      >
                        {credits?.availableCredits ?? 0} credits
                      </Typography>
                    </Box>
                  </Box>
                  {credits && credits.availableCredits < purchaseTheme.creditPrice && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => setCreditsPurchaseOpen(true)}
                        >
                          Buy Credits
                        </Button>
                      }
                    >
                      You need {purchaseTheme.creditPrice - credits.availableCredits} more credit{purchaseTheme.creditPrice - credits.availableCredits !== 1 ? 's' : ''} to unlock this theme.
                    </Alert>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {purchaseSuccess ? (
            <Button onClick={handlePurchaseCancel}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handlePurchaseCancel} disabled={isPurchasing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePurchaseConfirm}
                disabled={isPurchasing || !credits || credits.availableCredits < (purchaseTheme?.creditPrice ?? 0)}
                startIcon={isPurchasing ? <CircularProgress size={16} /> : <CartIcon />}
              >
                {isPurchasing ? 'Purchasing...' : 'Unlock & Apply'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Theme Full Preview Dialog */}
      {previewTheme && (
        <ThemeFullPreview
          theme={previewTheme}
          open={!!previewTheme}
          onClose={() => setPreviewTheme(null)}
          actions={
            <>
              <Button onClick={() => setPreviewTheme(null)}>Close</Button>
              {(previewTheme.owned || previewTheme.isFree) ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    onSelect(previewTheme.id);
                    setPreviewTheme(null);
                    setDialogOpen(false);
                  }}
                >
                  Use Theme
                </Button>
              ) : enableInlinePurchase && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CartIcon />}
                  onClick={() => {
                    setPreviewTheme(null);
                    setPurchaseTheme(previewTheme);
                  }}
                  disabled={!credits || credits.availableCredits < previewTheme.creditPrice}
                >
                  {previewTheme.creditPrice ? `Purchase for ${previewTheme.creditPrice} Credit${previewTheme.creditPrice !== 1 ? 's' : ''}` : 'Purchase'}
                </Button>
              )}
            </>
          }
        />
      )}

      {/* In-context Credits Purchase Modal */}
      {purchaseTheme && (
        <CreditsPurchaseModal
          open={creditsPurchaseOpen}
          onClose={() => setCreditsPurchaseOpen(false)}
          onSuccess={() => {
            setCreditsPurchaseOpen(false);
            refreshCredits();
          }}
          requiredCredits={purchaseTheme.creditPrice ?? 1}
          contextMessage={`Unlocking the "${purchaseTheme.name}" theme requires ${purchaseTheme.creditPrice ?? 1} credit${(purchaseTheme.creditPrice ?? 1) !== 1 ? 's' : ''}.`}
        />
      )}
    </Box>
  );
};

export default ThemeSelector;
